'use client'

import React, { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { CheckCircle2, XCircle, AlertCircle, Info, Copy, Check } from 'lucide-react'
import { cn } from '@/lib/utils'

export type ResponseDialogVariant = 'success' | 'error' | 'warning' | 'info'

export interface ResponseDialogButton {
  label: string
  onClick: () => void
  variant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link'
  className?: string
}

export interface ResponseDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  variant?: ResponseDialogVariant
  title: string
  description: string
  copyText?: string
  copyLabel?: string
  primaryButton?: ResponseDialogButton
  secondaryButton?: ResponseDialogButton
  showCloseButton?: boolean
  className?: string
}

const variantConfig = {
  success: {
    icon: CheckCircle2,
    iconColor: 'text-success-green',
    iconBgColor: 'bg-success-green/10',
  },
  error: {
    icon: XCircle,
    iconColor: 'text-destructive',
    iconBgColor: 'bg-destructive/10',
  },
  warning: {
    icon: AlertCircle,
    iconColor: 'text-warning-yellow',
    iconBgColor: 'bg-warning-yellow/10',
  },
  info: {
    icon: Info,
    iconColor: 'text-primary-blue',
    iconBgColor: 'bg-primary-blue/10',
  },
}

export function ResponseDialog({
  open,
  onOpenChange,
  variant = 'success',
  title,
  description,
  copyText,
  copyLabel = 'Copy',
  primaryButton,
  secondaryButton,
  showCloseButton = true,
  className,
}: ResponseDialogProps) {
  const [copied, setCopied] = useState(false)

  const config = variantConfig[variant]
  const Icon = config.icon

  const handleCopy = async () => {
    if (!copyText) return

    try {
      await navigator.clipboard.writeText(copyText)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  const handleClose = () => {
    onOpenChange(false)
    // Reset copied state when dialog closes
    setTimeout(() => setCopied(false), 300)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn('sm:max-w-md', className)}>
        <DialogHeader>
          {/* Icon */}
          <div className='flex justify-center mb-4'>
            <div className={cn('rounded-full p-3', config.iconBgColor)}>
              <Icon className={cn('h-8 w-8', config.iconColor)} />
            </div>
          </div>

          {/* Title */}
          <DialogTitle className='text-center text-xl'>{title}</DialogTitle>

          {/* Description */}
          <DialogDescription className='text-center text-base pt-2'>
            {description}
          </DialogDescription>
        </DialogHeader>

        {/* Copy Text Section */}
        {copyText && (
          <div className='space-y-2 py-4'>
            <p className='text-sm font-medium text-muted-foreground text-center'>
              {copyLabel}
            </p>
            <div className='flex items-center gap-2 p-3 bg-muted rounded-md'>
              <code className='flex-1 text-sm font-mono text-center'>
                {copyText}
              </code>
              <Button
                type='button'
                variant='ghost'
                size='icon'
                onClick={handleCopy}
                className='h-8 w-8 shrink-0'
              >
                {copied ? (
                  <Check className='h-4 w-4 text-success-green' />
                ) : (
                  <Copy className='h-4 w-4' />
                )}
                <span className='sr-only'>
                  {copied ? 'Copied' : 'Copy to clipboard'}
                </span>
              </Button>
            </div>
          </div>
        )}

        {/* Footer with Buttons */}
        <DialogFooter className='flex-col sm:flex-row gap-2 sm:gap-2'>
          {secondaryButton && (
            <Button
              type='button'
              variant={secondaryButton.variant || 'outline'}
              onClick={secondaryButton.onClick}
              className={cn('w-full sm:w-auto', secondaryButton.className)}
            >
              {secondaryButton.label}
            </Button>
          )}
          {primaryButton && (
            <Button
              type='button'
              variant={primaryButton.variant || 'default'}
              onClick={primaryButton.onClick}
              className={cn('w-full sm:w-auto', primaryButton.className)}
            >
              {primaryButton.label}
            </Button>
          )}
          {showCloseButton && !primaryButton && !secondaryButton && (
            <Button
              type='button'
              variant='default'
              onClick={handleClose}
              className='w-full sm:w-auto'
            >
              Close
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

